import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BcConfigService } from 'common/config/provider';
import { GenesysCloudAuthService } from 'common/auth/genesys-cloud-auth.service';
import { Bold360Service } from 'common/auth/bold360.service';
import { AuthenticationModes } from 'common/config/models/authentication-modes';
import { WindowService } from 'shared/services/window/window.service';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { AuthenticationResult, AuthenticationResultFailure } from 'common/auth/models/authentication-result';
import { bcAuthEvents } from 'common/common/constants/auth-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectUser } from 'common/store/genesys-cloud/users/selectors/user.selectors';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import {
  selectHasOrgPairingIntegration,
  selectOrgPairingLoaded
} from 'common/store/genesys-cloud/integrations/selectors/integrations.selectors';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CommonAuthService {
  public readonly isGenesysEnabled: boolean;

  private readonly _isHybridDisabled: boolean;
  private readonly _user$ = this.store.select(selectUser);
  private readonly _orgPairingLoaded$ = this.store.select(selectOrgPairingLoaded);
  private readonly _hasOrgPairingIntegration$ = this.store.select(selectHasOrgPairingIntegration);
  private _isHybridPromise: Promise<boolean>;

  constructor(private readonly _genesysCloudAuth: GenesysCloudAuthService,
              private readonly _bold360Service: Bold360Service,
              private readonly _config: BcConfigService,
              private readonly _stateService: StateServiceWrapper,
              private readonly _$window: WindowService,
              private readonly _rxEmitter: RxEmitter,
              private readonly store: Store) {
    this.isGenesysEnabled = this._config.authenticationMode === AuthenticationModes.GENESYS;
    this._isHybridDisabled = this._config.current.isGenesysHybridModeDisabled;

    this._rxEmitter.subscribe(MessageBusEvents.StreamLoginRequired, (event, reason: string | { token: string }) => {
      this.logout(reason);
    });

    this._rxEmitter.subscribe(MessageBusEvents.BcAuthLogout, () => {
      this.logout();
    });
  }

  public isBoldEnabled(): Promise<boolean> {
    if(!this.isGenesysEnabled) {
      return Promise.resolve(true);
    }

    if(this._isHybridDisabled) {
      return Promise.resolve(false);
    }

    return this.isHybrid();
  }

  async init(isSSO: boolean, activatedRouteSnapshot: ActivatedRouteSnapshot): Promise<boolean> {
    if (this.isGenesysEnabled) {
      const result = await this._genesysCloudAuth.init().pipe(first()).toPromise();
      const isSuccess = await this.processGenesysResult(result, activatedRouteSnapshot);
      if (isSuccess) {
        this._rxEmitter.send(bcAuthEvents.loginSuccess, true);
      }
      return isSuccess;
    }
    const result = await this._bold360Service.init(isSSO, activatedRouteSnapshot);
    const isSuccess = await this.processBold360OnlyResult(result);
    if (isSuccess) {
      this._rxEmitter.send(bcAuthEvents.loginSuccess, true);
    }
    return isSuccess;
  }

  canActivateRoute(): boolean {
    if (this.isGenesysEnabled) {
      return this._genesysCloudAuth.canActivateRoute();
    }
    return this._bold360Service.canActivateRoute();
  }

  isLoggedIn(): boolean {
    if (this.isGenesysEnabled) {
      return this._genesysCloudAuth.isLoggedIn();
    }
    return this._bold360Service.isLoggedIn();
  }

  async logout(reason?: string | { token: string }, returnUrl?: string): Promise<void> {
    if (this.isGenesysEnabled) {
      if (await this.isHybrid()) {
        await this._bold360Service.silentLogout(reason);
      }
      const url = await this._genesysCloudAuth.logout();
      this.redirect(url);
      return;
    }
    return this._bold360Service.logout(reason, returnUrl); // returnUrl is only valid in Bold-only Automation test flow
  }

  async isMultiAccount(): Promise<boolean> {
    return !this.isGenesysEnabled && this._bold360Service.isMultiAccount();
  }

  canChangePassword(): boolean {
    return !this.isGenesysEnabled && this._bold360Service.canChangePassword();
  }

  async getUserName(): Promise<string> {
    if (this.isGenesysEnabled) {
      return this.getGenesysUser();
    }
    return this._bold360Service.getUserName();
  }

  async hasDigitalDesktopPermission(): Promise<boolean> {
    await firstValueFrom(this._user$.pipe(filter(Boolean)));
    return this._genesysCloudAuth.isDigitalDesktopUiAuthorized();
  }

  isHybrid(): Promise<boolean> {
    if (!this._isHybridPromise) {
      this._isHybridPromise = new Promise(async (resolve) => {
        if (this._isHybridDisabled) {
          resolve(false);
          return;
        }
        const isOrgPaired = await this.hasActiveOrgPairingIntegration();
        if (!isOrgPaired) {
          resolve(false);
          return;
        }
        const isUserPaired = await this.isUserPaired();
        resolve(isUserPaired);
      });
    }
    return this._isHybridPromise;
  }

  private async processBold360OnlyResult(result: AuthenticationResult): Promise<boolean> {
    if (result.reload) {
      this.reload();
      return false;
    } else if (result.newState) {
      await this._stateService.go(result.newState);
      return false;
    } else if (result.redirectUrl) {
      this.redirect(result.redirectUrl);
      return false;
    }
    if (result instanceof AuthenticationResultFailure) {
      await this._bold360Service.logout();
      return false;
    }
    return true;
  }

  private async processGenesysResult(result: AuthenticationResult, activatedRouteSnapshot: ActivatedRouteSnapshot) {
    if (result.redirectUrl) {
      this.redirect(result.redirectUrl);
      return false;
    }

    if (await this.isHybrid()) {
      return this.doHybridLogin(activatedRouteSnapshot);
    }
    return true;
  }

  private async doHybridLogin(activatedRouteSnapshot: ActivatedRouteSnapshot): Promise<boolean> {
    const result = await this._bold360Service.init(false, activatedRouteSnapshot);
    return this.processHybridResult(result);
  }

  private async processHybridResult(result: AuthenticationResult): Promise<boolean> {
    if (result instanceof AuthenticationResultFailure) {
      result = await this._bold360Service.redeemAccessToken();
      if (result instanceof AuthenticationResultFailure && result.redirectUrl) {
        this.redirect(result.redirectUrl);
        return false;
      }
    } else if (result.reload) {
      this.reload();
      return false;
    }
    return true;
  }

  private async isUserPaired(): Promise<boolean> {
    const accessToken = await this._genesysCloudAuth.getAccessToken();

    return this._bold360Service.isUserPaired(accessToken);
  }

  private hasActiveOrgPairingIntegration(): Promise<boolean> {
    return this._orgPairingLoaded$.pipe(
      filter(Boolean),
      switchMap(() => this._hasOrgPairingIntegration$),
      first()
    ).toPromise();
  }

  private getGenesysUser(): Promise<string> {
    return this._user$.pipe(
      filter(user => !!user),
      map(userInfo => userInfo.name),
      first()
    ).toPromise();
  }

  private redirect(url: string): void {
    this._$window.location.href = url;
  }

  private reload(): void {
    this._$window.location.reload();
  }
}
